import * as React from 'react';
import {
	auditService,
	GET_CASE_STEPS_STATUS,
	GET_DOCUMENT_REQUIRED,
	GET_LAWYER_CASE_DETAILS,
} from '@/api';
import { caseApplicantService } from '@/api/caseApplicant';
import { Button, LoaderScreen } from '@/components/common';
import Icons from '@/const/icons';
import { useNotification } from '@/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import {
	ApprovePhase,
	DocumentRequiredPayload,
	DocumentRequiredPayloadItem,
	RequestDocumentData,
	RequestDocumentItemMap,
	StepStatusCase,
} from '@/interfaces';
import { CaseContext } from '@/context/CaseContext';
import { useRequestDocumentModals } from '@/hooks';
import { DocumentCard } from './DocumentCard';
import { getValidCaseId } from '@/utils';

interface DocumentRequiredProps {
	phaseId: number;
	stepCase?: StepStatusCase;
}

export const DocumentRequired: React.FC<DocumentRequiredProps> = ({
	phaseId,
	stepCase,
}) => {
	const router = useRouter();
	const caseId = getValidCaseId(router.query.id);
	const id = caseId;
	const queryClient = useQueryClient();
	const { isCaseReadOnly, caseDetails, setSaveStatus } =
		React.useContext(CaseContext);
	const { toastError } = useNotification();
	const [isLoadingAutosaved, setIsLoadingAutosaved] = React.useState(false);
	const [selectedUserIds, setSelectedUserIds] = React.useState<
		(string | null)[]
	>([]);
	const [editingDocument, setEditingDocument] =
		React.useState<DocumentRequiredPayloadItem | null>(null);

	const {
		data: documentRequired,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [GET_DOCUMENT_REQUIRED],
		queryFn: () => caseApplicantService.getDocumentRequired(id as string),
		enabled: !!id,
	});

	React.useEffect(() => {
		setSaveStatus({ isSaving: isLoadingAutosaved });
	}, [isLoadingAutosaved, setSaveStatus]);

	const handleApprove = async () => {
		setIsLoadingAutosaved(true);
		try {
			if (id && stepCase) {
				const data: ApprovePhase = {
					case_id: id as string,
					lawyer_stepper_id: phaseId,
					stepper_form_id: stepCase.step_id,
				};
				await auditService.postApprovePhase(data);
				refetch();
				if (caseId) {
					queryClient.refetchQueries({
						queryKey: [GET_CASE_STEPS_STATUS, caseId],
					});
					queryClient.refetchQueries({
						queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
					});
				}
			}
		} catch (error) {
			toastError(
				'An error occurred while confirming approval. Please try again.'
			);
		} finally {
			setIsLoadingAutosaved(false);
		}
	};

	const onSubmit = React.useCallback(
		async (data: RequestDocumentData) => {
			try {
				const criteria = data.request_question.map((item) => ({
					...item,
					criteria_type: RequestDocumentItemMap[item.criteria_type],
				}));

				const isEditing = editingDocument != null;

				if (isEditing && editingDocument?.id) {
					const updateData = {
						name: data.document_name,
						criteria,
						skip_document: data?.skip_document,
					};
					await caseApplicantService.updateCurrentDocument(
						editingDocument.id,
						updateData as any
					);
				} else {
					const existingDocuments = (documentRequired?.data ?? []).map(
						(doc) => ({
							name: doc.name,
							criteria: doc.criteria,
							skip_document: doc?.skip_document ?? false,
							instructions: doc.instructions ?? null,
						})
					);

					const newData: DocumentRequiredPayload = {
						documents: [
							...existingDocuments,
							{
								name: data.document_name,
								criteria,
								skip_document: data.skip_document,
								instructions: null,
							},
						],
						is_document_required: true,
					};
					await caseApplicantService.putDocumentRequired(
						caseId as string,
						newData
					);
				}

				refetch();
				await handleApprove();
				setIsOpenRequestDocuments(false);
				setEditingDocument(null);
				if (caseId) {
					queryClient.refetchQueries({
						queryKey: [GET_CASE_STEPS_STATUS, caseId],
					});
					queryClient.refetchQueries({
						queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
					});
				}
			} catch (error) {
				toastError('The document could not be requested. Please try again.');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			caseId,
			refetch,
			queryClient,
			toastError,
			editingDocument,
			documentRequired,
		]
	);

	const removeDocument = async (id: number | undefined) => {
		if (!documentRequired?.data) return;
		const listDocuments = documentRequired.data.filter(
			(item) => item.id !== id
		);
		await onDelete(listDocuments);
	};

	const onDelete = async (listDocuments: DocumentRequiredPayloadItem[]) => {
		try {
			const cleanedDocuments = listDocuments.map((doc) => ({
				name: doc.name,
				criteria: doc.criteria,
				skip_document: doc.skip_document ?? false,
				instructions: doc.instructions ?? null,
			}));

			await caseApplicantService.putDocumentRequired(String(caseId), {
				documents: cleanedDocuments,
				is_document_required: cleanedDocuments.length > 0,
			});
			if (cleanedDocuments.length > 0) {
				await handleApprove();
			}
			refetch();
			if (caseId) {
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, caseId],
				});
				queryClient.refetchQueries({
					queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
				});
			}
		} catch (error) {
			toastError('An error occurred while deleting. Please try again.');
		}
	};

	const {
		onOpenRequestDocuments,
		setIsOpenRequestDocuments,
		renderRequestDocumentModals,
	} = useRequestDocumentModals({
		caseName: caseDetails?.title || '',
		isCaseReadOnly,
		isStepApproved: false,
		onSubmit,
		selectedUserIds,
		setSelectedUserIds,
		existingDocuments: documentRequired?.data ?? [],
		initialData: editingDocument,
		onCloseRequestDocuments: () => setEditingDocument(null),
	});

	const handleEditDocument = (doc: DocumentRequiredPayloadItem) => {
		if (isCaseReadOnly) return;
		setEditingDocument(doc);
		setIsOpenRequestDocuments(true);
	};

	return (
		<>
			{isLoading ? (
				<LoaderScreen />
			) : (
				<>
					{documentRequired?.data.map((d) => (
						<DocumentCard
							key={d.id}
							type="document-requested"
							title={d.name}
							badges={
								d.criteria
									?.filter((c) => c.criteria_type !== 'prompt')
									.map((c) => c.criteria_type) ?? []
							}
							prompts={
								d.criteria
									?.filter((c) => c.criteria_type === 'prompt')
									.map((c) => c.prompt ?? '')
									.filter((p) => p.length > 0) ?? []
							}
							isCompleted={true}
							onDelete={() => removeDocument(d.id)}
							onDoubleClick={() => handleEditDocument(d)}
							className="hover:!border-main-blue"
							documentInfo={d}
						/>
					))}
					<Button
						disabled={isCaseReadOnly}
						label="Request Document"
						iconLeft
						icon={Icons.plus2}
						className="mt-6 !py-2 h-12 "
						decoration="line-primary"
						size="fit"
						classNameButton="!size-[18px] mr-4"
						onClick={onOpenRequestDocuments}
					/>
				</>
			)}
			{renderRequestDocumentModals}
		</>
	);
};
